// GroupedTabs — desktop dropdown nav + mobile flat strip. Extracted from App.js.
import React from "react";
import { TAB_GROUPS } from "./tabs";

export default function GroupedTabs({ tab, setTab, tabsForRole, byId, badges = {} }) {
  const [openGroup, setOpenGroup] = React.useState(null);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenGroup(null); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);
  // v200.50 (2026-08-14) — `hidden:true` tabs are excluded from every
  // nav surface (desktop dropdown + mobile flat strip). Deep links still
  // work; only the visible menu entry disappears. Guarded by
  // test_nav_hidden_flag_enforced.py.
  const allowed = new Set(tabsForRole.filter((t) => !t.hidden).map((t) => t.id));
  const groups = TAB_GROUPS
    .map((g) => {
      const primaryAllowed = allowed.has(g.primary);
      const children = g.children.filter((c) => allowed.has(c));
      // Aggregate badges from primary + children for this group.
      const badgeCount = (badges[g.primary] || 0) + children.reduce((sum, cid) => sum + (badges[cid] || 0), 0);
      return primaryAllowed || children.length > 0 ? { ...g, children, primaryAllowed, badgeCount } : null;
    })
    .filter(Boolean);
  return (
    <>
      {/* Desktop: grouped with dropdowns */}
      <div className="tabs tabs-grouped" ref={ref} data-testid="tabs-grouped">
        {groups.map((g) => {
          const isActive = tab === g.primary || g.children.includes(tab);
          const hasChildren = g.children.length > 0;
          return (
            <div key={g.id} className={`tab-group ${openGroup === g.id ? "open" : ""}`}>
              <button
                type="button"
                data-testid={`tab-group-${g.id}`}
                className={`tab ${isActive ? "on" : ""}`}
                style={{ position: "relative" }}
                aria-haspopup={hasChildren ? "menu" : undefined}
                aria-expanded={hasChildren ? (openGroup === g.id) : undefined}
                onClick={() => {
                  if (hasChildren) {
                    // Touch-friendly: toggle dropdown. User picks primary or a child from inside.
                    setOpenGroup((cur) => (cur === g.id ? null : g.id));
                  } else if (g.primaryAllowed) {
                    setTab(g.primary);
                    setOpenGroup(null);
                  }
                }}
                onPointerEnter={(e) => {
                  // Only auto-open on real mouse hover; on touch (iPad), wait for the tap (click).
                  // Without this guard, iPad fires pointerenter+click → dropdown opens then immediately toggles closed.
                  if (hasChildren && e.pointerType === "mouse") {
                    setOpenGroup(g.id);
                  }
                }}
              >
                {g.label}{hasChildren && <span style={{ marginInlineStart: 4, opacity: 0.6, fontSize: 10 }}>▾</span>}
                {g.badgeCount > 0 && (
                  <span data-testid={`tab-badge-${g.id}`} aria-label={`${g.badgeCount} عنصر جديد`}
                    style={{
                      position: "absolute", top: -4, insetInlineStart: -6,
                      background: "#dc2626", color: "#fff", borderRadius: 999,
                      fontSize: 10, padding: "1px 6px", fontWeight: 800,
                      boxShadow: "0 0 0 2px #fff",
                    }}>{g.badgeCount > 99 ? "99+" : g.badgeCount}</span>
                )}
              </button>
              {hasChildren && openGroup === g.id && (
                <div className="tab-dropdown" onMouseLeave={() => setOpenGroup(null)}>
                  {g.primaryAllowed && byId[g.primary] && (
                    <button
                      key={g.primary}
                      type="button"
                      data-testid={`tab-${g.primary}`}
                      className={`tab-sub ${tab === g.primary ? "on" : ""}`}
                      onClick={() => { setTab(g.primary); setOpenGroup(null); }}
                    >
                      {byId[g.primary].label}
                    </button>
                  )}
                  {g.children.map((cid) => {
                    const ct = byId[cid];
                    if (!ct) return null;
                    return (
                      <button
                        key={cid}
                        type="button"
                        data-testid={`tab-${cid}`}
                        className={`tab-sub ${tab === cid ? "on" : ""}`}
                        onClick={() => { setTab(cid); setOpenGroup(null); }}
                      >
                        {ct.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: flat scrollable strip — hidden on desktop via CSS */}
      <div className="tabs tabs-flat" data-testid="tabs-flat">
        {tabsForRole.filter((t) => !t.hidden).map((t) => {
          const badge = badges[t.id] || 0;
          return (
            <button
              key={t.id}
              data-testid={`tab-flat-${t.id}`}
              className={`tab ${tab === t.id ? "on" : ""}`}
              style={{ position: "relative" }}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {badge > 0 && (
                <span style={{
                  position: "absolute", top: -3, insetInlineStart: -3,
                  background: "#dc2626", color: "#fff", borderRadius: 999,
                  fontSize: 9, padding: "1px 5px", fontWeight: 800,
                }}>{badge > 99 ? "99+" : badge}</span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
